import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  TextField,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { UserApi } from "../../../apis";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import WeekPicker from "../phases/weekPicker";
import dayjs from "dayjs";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Create a SortableRow component that wraps both the main row and description row
function SortableTimeRow({ row, rowIndex, isEven, children, descriptionRow }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowIndex });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <React.Fragment>
      <TableRow 
        ref={setNodeRef} 
        style={style} 
        {...attributes}
        sx={{ 
          backgroundColor: isEven ? "#F1F4F9" : "#ffffff",
          outline: "none !important",
          borderLeft: "1px solid rgba(224, 224, 224, 1)",
          borderRight: "1px solid rgba(224, 224, 224, 1)"
        }}
      >
        {/* Drag handle */}
        <TableCell sx={{ cursor: 'grab', borderBottom: "none !important" }} {...listeners}>
          <DragIndicatorIcon />
        </TableCell>
        
        {/* Rest of row content */}
        {children}
      </TableRow>
      
      {/* Description row - part of the same draggable unit */}
      {descriptionRow}
    </React.Fragment>
  );
}

const AddTime = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [totalHours, setTotalHours] = useState("");
  const [timeEntries, setTimeEntries] = useState([]);
  const [note, setNote] = useState("");
  const [statusMessage, setStatusMessage] = useState(
    "Your time sheet status is pending."
  );
  const [statusChangeTrigger, setStatusChangeTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [timeSheetId, setTimeSheetId] = useState("");
  const [isSubmited, setIsSubmited] = useState(false);

  let userId = null;
  let userName = "";
  try {
    const userString = localStorage.getItem("user");
    const user = JSON.parse(userString);
    userId = user?.id;
    userName = user?.name;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
  }

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await UserApi.getAssignedProjectsById(userId);
      const fetchedProjects = response.data.data.projects.map((proj) => ({
        projectName: proj.name,
        phases: proj.phases || [],
      }));

      setProjects(fetchedProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects.");
    } finally {
      setLoading(false);
    }
  };

  const getStartOfWeek = (date, useLegacyFormat = false) => {
    const currentDate = dayjs(date);
        const cutoffDate = dayjs('2025-10-12'); // October 12, 2025
    
        // If legacy format or date is before October 12, 2025, use Sunday as start
    if (useLegacyFormat || currentDate.isBefore(cutoffDate, 'day')) {
      // Calculate Sunday of the week (legacy format)
      const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.
      return currentDate.subtract(dayOfWeek, 'day').startOf('day'); // Go back to Sunday
    }
    
    // Use Monday as start (new format) - this uses the global Day.js config
    return currentDate.startOf("week");
  };

  const getWeekDays = (selectedDate) => {
        // Check if the selected week is before October 12, 2025
        const cutoffDate = dayjs('2025-10-12');
    const isOldData = dayjs(selectedDate).isBefore(cutoffDate, 'day');
    const startOfWeek = getStartOfWeek(selectedDate, isOldData);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = startOfWeek.add(i, "day");
      const dayFormatted = {
        day: day.format("ddd"),
        dateString: day.format("DD-MMM"),
      };
      weekDays.push(dayFormatted);
    }
    return weekDays;
  };

  const [newRows, setNewRows] = useState([
    {
      projectName: "",
      phaseName: "",
      hours: getWeekDays(selectedDate).reduce(
        (acc, day) => ({ ...acc, [day.dateString]: 0 }), // Ensure hours are initialized to 0
        {}
      ),
      notes: "",
      description: "", // Added description field
    },
  ]);

  // New Monday-Sunday week (current)
  const getWeekDates = (selectedDate) => {
    const cutoffDate = dayjs('2025-10-12');
    const isOldData = dayjs(selectedDate).isBefore(cutoffDate, 'day');
    const start = getStartOfWeek(selectedDate, isOldData);
    const end = start.add(6, "day");
    return { startDate: start.format("DD-MM-YYYY"), endDate: end.format("DD-MM-YYYY") };
  };

  // Old Sunday-Saturday week (for backward compatibility)
  const getLegacyWeekDates = (selectedDate) => {
    const currentDate = dayjs(selectedDate);
    const dayOfWeek = currentDate.day(); // 0 = Sunday, 1 = Monday, etc.
    const start = currentDate.subtract(dayOfWeek, 'day').startOf('day'); // Go back to Sunday
    const end = start.add(6, "day");
    return { startDate: start.format("DD-MM-YYYY"), endDate: end.format("DD-MM-YYYY") };
  };

  useEffect(() => {
    if (selectedDate) {
      fetchProjects();
    }
  }, [selectedDate]);

  // Handle changes in hours
  const handleHourChange = (rowIndex, date, value, isNewRow = false) => {
    if (isNewRow) {
      const updatedNewRows = [...newRows];

      if (!updatedNewRows[rowIndex].hours) {
        updatedNewRows[rowIndex].hours = {};
      }

      updatedNewRows[rowIndex].hours[date] = parseFloat(value);

      setNewRows(updatedNewRows);
    } else {
      const updatedTimeEntries = [...timeEntries];

      if (!updatedTimeEntries[rowIndex].hours) {
        updatedTimeEntries[rowIndex].hours = {};
      }

      updatedTimeEntries[rowIndex].hours[date] = parseFloat(value);

      setTimeEntries(updatedTimeEntries);
    }
  };

  // Handle changes in description
  const handleDescriptionChange = (rowIndex, value, isNewRow = false) => {
    if (isNewRow) {
      const updatedNewRows = [...newRows];
      updatedNewRows[rowIndex].description = value;
      setNewRows(updatedNewRows);
    } else {
      const updatedTimeEntries = [...timeEntries];
      updatedTimeEntries[rowIndex].description = value;
      setTimeEntries(updatedTimeEntries);
    }
  };

  const handleNotesChange = (e) => {
    setNote(e.target.value);
  };

  // Get timesheet for selected week
  const getTimeSheetUser = async (selectedDate) => {
    if (!selectedDate) {
      console.error("Selected date is invalid.");
      return;
    }
    // set description to empty
    setNote("");
    setTotalHours(0);
    const { startDate, endDate } = getWeekDates(selectedDate);
    const parsedStartDate = parseDate(startDate);
    const parsedEndDate = parseDate(endDate);

    if (!parsedStartDate || !parsedEndDate) {
      console.error("Invalid date values detected.");
      setError("Invalid week start or end date.");
      return;
    }

    const formattedStartDate = formatDate(parsedStartDate);
    const formattedEndDate = formatDate(parsedEndDate);

    const submissionDetails = {
      userId: userId,
      weekStart: formattedStartDate,
      weekEnd: formattedEndDate,
    };

    if (!userId) {
      console.error("User ID is missing.");
      setError("User ID is required.");
      return;
    }

    try {
      const response = await UserApi.getTimeSheetByPayload(submissionDetails);
      const totalHours = response?.data?.totalHours || 0;
      const note = response?.data?.notes;
      const timesheetId = response?.data?.id;
      const status = response?.data?.status;
      const isSubmited = response?.data?.isSubmited;

      if (status === "approved") {
        setStatusMessage("Time sheet is approved.");
      } else if (status === "rejected") {
        setStatusMessage("Time sheet is rejected.");
      } else {
        setStatusMessage("Your time sheet status is pending.");
      }
      setIsSubmited(isSubmited);
      setTimeSheetId(timesheetId);
      setNote(note);

      let rows = response?.data?.rows || [];
      if (typeof rows === "string") {
        rows = JSON.parse(rows);
      }

      if (rows.length === 0) {
        setNewRows([
          {
            projectName: "",
            phaseName: "",
            hours: getWeekDays(selectedDate).reduce(
              (acc, day) => ({ ...acc, [day.dateString]: 0 }), // Ensure hours are initialized to 0
              {}
            ),
            notes: "",
            description: "", // Added description field
          },
        ]);
        setTimeEntries([]);
        setTotalHours(0);
        setError("No data available for the selected week.");
      } else {
        rows.forEach((row) => {
          const hours = {};
          getWeekDays(selectedDate).forEach((day) => {
            const dateString = day.dateString;
            hours[dateString] = row.hours[dateString] || 0;
          });
          row.hours = hours;
        });
        setTimeEntries(rows);
        setNewRows([]);
        setTotalHours(totalHours);
      }
    } catch (error) {
      setTotalHours(0);
      setNewRows([]);
      setNote("");
      setTimeEntries([]);
      console.error("Error fetching timesheet data:", error);
      setError("An error occurred while fetching the timesheet data.");
      setStatusMessage("Your time sheet status is pending.");
    }
  };

  useEffect(() => {
    if (selectedDate) {
      getTimeSheetUser(selectedDate);
    }
  }, [selectedDate]);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDate = (dateString) => {
    const dateParts = dateString.split("-");
    if (dateParts.length === 3) {
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const year = parseInt(dateParts[2], 10);

      return new Date(year, month, day);
    }
    return null;
  };

  const handleExportPDF = async () => {
    const input = document.getElementById("timesheet");

    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const padding = 10;

    const pageWidth = 210;
    const imgWidth = pageWidth - 2 * padding;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const imgPositionX = padding;
    const imgPositionY = 6;

    pdf.addImage(
      imgData,
      "PNG",
      imgPositionX,
      imgPositionY,
      imgWidth,
      imgHeight
    );
    pdf.save("timesheet.pdf");
  };

  useEffect(() => { }, [statusMessage, statusChangeTrigger]);

  const getPhasesForProject = (projectName) => {
    const project = projects.find(
      (project) => project.projectName === projectName
    );
    return project ? project.phases : [];
  };

  const calculateTotalHours = (newRows) => {
    let total = 0;
    newRows.forEach((entry) => {
      Object.values(entry?.hours).forEach((hours) => {
        total += Number(hours) || 0; // Convert to number, default to 0 if empty
      });
    });

    return total;
  };

  const handleSubmit = async (isSubmited = true) => {
    const { startDate, endDate } = getWeekDates(selectedDate);
    let rows;
    if (newRows) {
      rows = newRows.map((entry) => {
        const hours = {};

        for (let i = 0; i < 7; i++) {
          const dayDate = moment(startDate, "DD-MM-YYYY")
            .add(i, "days")
            .format("YYYY-MM-DD");
          console.log("Day date:", dayDate);
          hours[dayDate] = entry?.hours[dayDate] || 0;
        }
        return {
          projectName: entry?.projectName,
          phaseName: entry?.phaseName,
          hours: entry.hours,
          notes: note || "",
          description: entry.description || "", // Added description field
          isSubmited: isSubmited,
        };
      });
    }
    try {
      await getTimeSheetUser(selectedDate);

      if (timeSheetId) {
        const existingRows = timeEntries;
        const mergedRows = [...existingRows, ...rows];
        const totalHours = calculateTotalHours(mergedRows);
        console.log("Total hours:", totalHours);
        if (isSubmited) {
          mergedRows.forEach((entry) => {
            entry.isSubmited = isSubmited;
          });
        }
        await UserApi.updateTimeSheet({
          id: timeSheetId,
          userId: userId,
          rows: mergedRows,
          notes: note,
          weekStart: startDate,
          weekEnd: endDate,
          totalHours: totalHours,
          isSubmited: isSubmited,
        });

        setTimeEntries(mergedRows);
        setTotalHours(totalHours);
        setNote(note);
        setTimeSheetId(timeSheetId);
        getTimeSheetUser(selectedDate);
      } else {
        const totalHours = calculateTotalHours(rows);
        const createResponse = await UserApi.createTimeSheet({
          userId: userId,
          rows: rows,
          notes: note,
          weekStart: startDate,
          weekEnd: endDate,
          totalHours: totalHours,
          isSubmited: isSubmited,
        });

        setTimeEntries(rows);
        setTotalHours(totalHours);
        setNote(note);
        setTimeSheetId(createResponse.data.timeSheetId);
        setStatusMessage("Your new timesheet has been created.");
        getTimeSheetUser(selectedDate);
      }
    } catch (error) {
      console.error("Error handling timesheet submission:", error);
      setError("An error occurred while handling your timesheet.");
    }
  };

  const handleAddRow = () => {
    setNewRows((prevRows) => [
      ...prevRows,
      {
        projectName: "",
        phaseName: "",
        hours: getWeekDays(selectedDate).reduce(
          (acc, day) => ({ ...acc, [day.dateString]: 0 }),
          {}
        ),
        notes: "",
        description: "", // Added description field
      },
    ]);
  };

  const handleWeekChange = (direction) => {
    let newDate = dayjs(selectedDate);

    if (direction === "past") {
      newDate = newDate.subtract(7, "day");
    } else if (direction === "future") {
      newDate = newDate.add(7, "day");
    }

    setSelectedDate(newDate);
  };

  const handleSaveAsDraft = async () => {
    // call handleSubmit function with isSubmited set to false
    handleSubmit(false);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTimeEntries((items) => {
        const oldIndex = active.id;
        const newIndex = over.id;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  return (
    <Paper elevation={3} style={{ padding: 20, margin: "20px auto" }}>
      <div id="timesheet">
        <Typography variant="h4" align="center" gutterBottom>
          Add Weekly Time Sheet
        </Typography>

        <Grid mt={3} container spacing={2}>
          <Grid item xs={12} md={6} lg={6} xl={6}>
            <Typography>
              <strong>Username:</strong>
              {userName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <WeekPicker
              selectedDate={selectedDate}
              onDateChange={(date) => setSelectedDate(date)}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={6} xl={6}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
              onClick={() => handleWeekChange("past")}
            >
              Past
            </Button>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
            item xs={12} md={6} lg={6} xl={6}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
              onClick={() => handleWeekChange("future")}
            >
              Future
            </Button>
          </Grid>

          <Grid mt={3} item xs={12}>
            <Table >
                <TableHead sx={{bgcolor:"#00000017"}}>
                  <TableRow >
                    {timeEntries.length > 0 && <TableCell sx={{ width: 50 }}></TableCell>} {/* For drag handle - only show when there are entries */}
                    <TableCell>Project</TableCell>
                    <TableCell>Phase</TableCell>
                  {getWeekDays(selectedDate).map((day) => (
                    <TableCell key={day.dateString}>
                      {day.day} <br />
                      {day.dateString}
                    </TableCell>
                  ))}
                  {/* <TableCell>Description</TableCell> */}
                  {/* Added Description column */}
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={timeEntries.map((_, i) => i)}
                    strategy={verticalListSortingStrategy}
                  >
                    {timeEntries.map((row, rowIndex) => {
                      const isEven = rowIndex % 2 === 0;
                      const selectedProject = projects.find(
                        (project) => project.projectName === row.projectName
                      );
                      const filteredPhases = selectedProject
                        ? selectedProject.phases
                        : [];

                      return (
                        <SortableTimeRow
                          key={rowIndex}
                          row={row}
                          rowIndex={rowIndex}
                          isEven={isEven}
                          descriptionRow={
                            <TableRow sx={{ 
                              backgroundColor: isEven ? "#F1F4F9" : "#ffffff",
                              outline: "none !important",
                              borderLeft: "1px solid rgba(224, 224, 224, 1)",
                              borderRight: "1px solid rgba(224, 224, 224, 1)"
                            }}>
                              <TableCell colSpan={timeEntries.length > 0 ? 10 : 9}>
                                <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "15px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    Description
                                  </Typography>
                                  <TextField
                                    placeholder="Description"
                                    name="description"
                                    fullWidth
                                    multiline
                                    rows={1}
                                    disabled={row?.isSubmited}
                                    value={row.description || ""}
                                    onChange={(event) =>
                                      handleDescriptionChange(
                                        rowIndex,
                                        event.target.value
                                      )
                                    }
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          }
                        >
                          <TableCell sx={{borderBottom:"none !important"}}>
                            <FormControl fullWidth>
                              <Select
                                sx={{ width: "200px" }}
                                value={row.projectName || ""}
                                disabled={row?.isSubmited}
                                onChange={(event) => {
                                  const projectName = event.target.value;
                                  const selectedProject = projects.find(
                                    (project) => project.projectName === projectName
                                  );
                                  if (selectedProject) {
                                    setTimeEntries((prev) =>
                                      prev.map((entry, i) =>
                                        i === rowIndex
                                          ? {
                                            ...entry,
                                            projectName,
                                            phaseName: "",
                                          }
                                          : entry
                                      )
                                    );
                                  }
                                }}
                              >
                                <MenuItem value="" disabled>
                                  Select Project
                                </MenuItem>
                                {projects
                                  .slice()
                                  .sort((a, b) => a.projectName.localeCompare(b.projectName))
                                  .map((project) => (
                                    <MenuItem
                                      key={project.projectName}
                                      value={project.projectName}
                                    >
                                      {project.projectName}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </TableCell>

                          <TableCell sx={{borderBottom:"none !important"}}>
                            <FormControl fullWidth>
                              <Select
                                sx={{
                                  width: "200px",
                                  pointerEvents: !row?.isSubmited ? "auto" : "none",
                                  "& .MuiSelect-select": {
                                    color: row.phaseName ? "inherit" : "grey",
                                  },
                                }}
                                disabled={row?.isSubmited}
                                value={row.phaseName || ""}
                                onChange={(event) => {
                                  const selectedPhase = event.target.value;
                                  setTimeEntries((prev) =>
                                    prev.map((entry, i) =>
                                      i === rowIndex
                                        ? {
                                          ...entry,
                                          phaseName: selectedPhase,
                                        }
                                        : entry
                                    )
                                  );
                                }}
                              >
                                <MenuItem value="" disabled={row?.isSubmited}>
                                  Select Phase
                                </MenuItem>
                                {filteredPhases.length > 0 ? (
                                  filteredPhases.map((filteredPhase) => (
                                    <MenuItem
                                      key={filteredPhase.id}
                                      value={filteredPhase.name}
                                    >
                                      {filteredPhase.name}
                                    </MenuItem>
                                  ))
                                ) : (
                                  <MenuItem disabled>No phases available</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </TableCell>

                          {getWeekDays(selectedDate).map((day) => (
                            <TableCell key={day.dateString} sx={{borderBottom:"none !important"}}>
                              <TextField
                                type="number"
                                inputProps={{
                                  min: 0,
                                  step: 0.5,
                                  readOnly: row?.isSubmited,
                                }}
                                fullWidth
                                disabled={row?.isSubmited}
                                value={row.hours[day.dateString] || ""}
                                sx={{
                                  minWidth: 80,
                                  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                    { appearance: "none", margin: 0 },
                                  "& input[type=number]": {
                                    MozAppearance: "textfield",
                                  },
                                }}
                                onChange={(event) =>
                                  handleHourChange(rowIndex, day.dateString, event.target.value)
                                }
                              />
                            </TableCell>
                          ))}
                        </SortableTimeRow>
                      );
                    })}
                  </SortableContext>
                </DndContext>

                {newRows.map((row, rowIndex) => {
                 const isOdd = rowIndex % 2 !== 0;
                  return (
                    <>
                      <TableRow key={`new-${rowIndex}`} sx={{ backgroundColor: isOdd ? "#F1F4F9" : "#ffffff",outline:"none !important", borderLeft:"1px solid rgba(224, 224, 224, 1)",borderRight:"1px solid rgba(224, 224, 224, 1)"  }}>
                        {timeEntries.length > 0 && <TableCell sx={{borderBottom:"none !important"}}></TableCell>} {/* Empty cell for alignment when drag handle is present */}
                        <TableCell sx={{borderBottom:"none !important"}}>
                          <FormControl fullWidth>
                            <Select
                              sx={{ width: "200px" }}
                              value={row.projectName || ""}
                              onChange={(event) => {
                                const selectedProjectName = event.target.value;
                                setNewRows((prevRows) =>
                                  prevRows.map((newRow, i) =>
                                    i === rowIndex
                                      ? {
                                        ...newRow,
                                        projectName: selectedProjectName,
                                        phaseName: "",
                                        hours: getWeekDays(selectedDate).reduce(
                                          (acc, day) => {
                                            acc[day.dateString] = ""; // Initialize empty hours for each date
                                            return acc;
                                          },
                                          {}
                                        ),
                                      }
                                      : newRow
                                  )
                                );
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select Project
                              </MenuItem>
                              {/* {projects.map((project) => (
                              <MenuItem
                                key={project.projectName}
                                value={project.projectName}
                              >
                                {project.projectName}
                              </MenuItem>
                            ))} */}
                              {projects
                                .slice() // Create a shallow copy to avoid mutating the original array
                                .sort((a, b) => a.projectName.localeCompare(b.projectName)) // Sort alphabetically
                                .map((project) => (
                                  <MenuItem key={project.projectName} value={project.projectName}>
                                    {project.projectName}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        <TableCell  sx={{borderBottom:"none !important"}}>
                          <FormControl fullWidth>
                            <Select
                              sx={{
                                width: "200px",
                                "& .MuiSelect-select": {
                                  color: row.phaseName ? "inherit" : "grey",
                                },
                              }}
                              value={row.phaseName || ""}
                              onChange={(event) => {
                                const selectedPhase = event.target.value;
                                setNewRows((prevRows) =>
                                  prevRows.map((newRow, i) =>
                                    i === rowIndex
                                      ? { ...newRow, phaseName: selectedPhase }
                                      : newRow
                                  )
                                );
                              }}
                            >
                              <MenuItem value="" disabled>
                                Select Phase
                              </MenuItem>
                              {/* {getPhasesForProject(row.projectName)?.length > 0 ? (
                              getPhasesForProject(row.projectName).map(
                                (phase, index) => (
                                  <MenuItem key={index} value={phase.name}>
                                    {phase.name}
                                  </MenuItem>
                                )
                              )
                            ) : (
                              <MenuItem disabled>No phases available</MenuItem>
                            )} */}
                              {getPhasesForProject(row.projectName)?.length > 0 ? (
                                getPhasesForProject(row.projectName)
                                  .slice() // Create a shallow copy to avoid modifying the original array
                                  .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
                                  .map((phase, index) => (
                                    <MenuItem key={index} value={phase.name}>
                                      {phase.name}
                                    </MenuItem>
                                  ))
                              ) : (
                                <MenuItem disabled>No phases available</MenuItem>
                              )}

                            </Select>
                          </FormControl>
                        </TableCell>

                        {getWeekDays(selectedDate).map((day) => (
                          <TableCell key={day.dateString}  sx={{borderBottom:"none !important"}}>
                            <TextField
                              type="number"
                              inputProps={{
                                min: 0,
                                step: 0.5,
                              }}
                              fullWidth
                              value={row.hours[day.dateString] || ""}
                              onChange={(event) =>
                                handleHourChange(
                                  rowIndex,
                                  day.dateString,
                                  event.target.value,
                                  true
                                )
                              }
                              sx={{
                                minWidth: 80,
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                {
                                  appearance: "none",
                                  margin: 0,
                                },
                                "& input[type=number]": {
                                  MozAppearance: "textfield",
                                },
                              }}
                            />
                          </TableCell>
                        ))}

                      </TableRow>
                      <TableRow key={`new-${rowIndex}-description`} sx={{ backgroundColor: isOdd ? "#F1F4F9" : "#ffffff",borderLeft:"1px solid rgba(224, 224, 224, 1)",borderRight:"1px solid rgba(224, 224, 224, 1)" }}>
                        <TableCell colSpan={timeEntries.length > 0 ? 10 : 9}>
                          <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            <Typography
                              sx={{
                                fontSize: "15px"
                              }}
                              fontWeight={500}
                            >Description</Typography>
                            <TextField
                              placeholder="Description"
                              name="description"
                              fullWidth
                              multiline
                              rows={1}
                              value={row.description || ""}
                              onChange={(event) =>
                                handleDescriptionChange(
                                  rowIndex,
                                  event.target.value,
                                  true
                                )
                              }
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>

            <Button
              variant="contained"
              color="primary"
              onClick={handleAddRow}
              sx={{ marginTop: "16px" }}
            >
              Add Row
            </Button>
          </Grid>

          <Grid container sx={{ width: "100%" }}>
            <Box sx={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  boxSizing: "border-box",
                  justifyContent: "space-between",
                  padding: "10px",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "end",
                    gap: "6px",
                  }}
                >
                  <Button
                    onClick={() => {
                      handleSaveAsDraft();
                    }}
                    variant="contained"
                    sx={{
                      backgroundColor: "primay.dark",
                      color: "white",
                      "&:hover": { backgroundColor: "primary.main" },
                    }}
                  >
                    Save as Draft
                  </Button>
                  <Button
                    onClick={() => {
                      handleSubmit(true);
                    }}
                    variant="contained"
                    sx={{
                      backgroundColor: "secondary.dark",
                      color: "white",
                      "&:hover": { backgroundColor: "secondary.main" },
                    }}
                  >
                    Submit Time Sheet For Approval
                  </Button>

                  <Button
                    onClick={handleExportPDF}
                    variant="contained"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    Export As PDF
                  </Button>
                </div>
              </div>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="body1" align="center" mt={2}>
          Total Hours: {totalHours}
        </Typography>
      </div>
    </Paper>
  );
};

export default AddTime;